import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameLevel } from '../../types/game';
import { useGameEngine, getCompletedLevels } from '../../hooks/useGameEngine';
import { useCompleteLevel } from '../../hooks/useGameLevels';
import { ZoneA_Players } from './ZoneA_Players';
import { ZoneB_Center } from './ZoneB_Center';
import { ZoneC_Info } from './ZoneC_Info';
import { ValidationBanner } from './ValidationBanner';
import { TimelineScrubber } from './TimelineScrubber';
import { HypothesisControls } from './HypothesisControls';

interface GameBoardProps {
  level: GameLevel;
  walkthrough?: string;
}

export function GameBoard({ level, walkthrough }: GameBoardProps) {
  const { t } = useTranslation('game');
  const completeLevel = useCompleteLevel();
  const completedRef = useRef(false);
  const {
    state,
    setRole,
    cycleRole,
    toggleHint,
    checkSolution,
    resetLevel,
    setTimelineDay,
    pushHypothesis,
    popHypothesis,
    setFocusedSeat,
  } = useGameEngine(level);

  const maxDay = level.days.length;

  // Increment server counter once when player solves the puzzle (not on repeated visits)
  useEffect(() => {
    if (state.isSolved && !completedRef.current) {
      completedRef.current = true;
      completeLevel.mutate(level.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isSolved]);

  return (
    <div className="flex flex-col gap-4">
      {/* Timeline scrubber */}
      <TimelineScrubber
        maxDay={maxDay}
        visibleDay={state.visibleDay}
        onChange={setTimelineDay}
      />

      {/* Hypothesis controls */}
      <HypothesisControls
        stack={state.hypothesisStack}
        onPush={pushHypothesis}
        onPop={popHypothesis}
      />

      {/* Validation errors */}
      <ValidationBanner errors={state.validationErrors} />

      {/* Success overlay */}
      <AnimatePresence>
        {state.isSolved && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="p-5 rounded border border-mafia-gold/50 bg-mafia-gold/10 text-center"
          >
            <div className="font-display text-2xl text-mafia-gold mb-1">
              {t('board.success.title')}
            </div>
            <div className="text-sm text-mafia-paper-dim mb-4">
              {t('board.success.subtitle')}
            </div>
            <Link to="/game" className="btn-gold text-sm">
              {t('board.success.backToLobby')}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 3-zone layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,_1fr)_2fr_minmax(200px,_1fr)] gap-4 lg:gap-6 items-start">
        {/* Zone A: Players */}
        <ZoneA_Players
          assignments={state.assignments}
          onSetRole={setRole}
          errors={state.validationErrors}
          focusedSeat={state.focusedSeat}
          onFocus={setFocusedSeat}
          onBlur={() => setFocusedSeat(null)}
        />

        {/* Zone B: Info hub */}
        <ZoneB_Center
          nights={level.nights}
          days={level.days}
          sheriffClaims={level.sheriffClaims}
          assignments={state.assignments}
          focusedSeat={state.focusedSeat}
          visibleDay={state.visibleDay}
        />

        {/* Zone C: Analytics */}
        <ZoneC_Info
          level={level}
          assignments={state.assignments}
          checkedHints={state.checkedHints}
          onToggleHint={toggleHint}
          visibleDay={state.visibleDay}
          onCheckSolution={checkSolution}
          onReset={resetLevel}
          walkthrough={walkthrough}
          onShowSolution={() => completeLevel.mutate(level.id)}
        />
      </div>
    </div>
  );
}
